/**
 * CQC-F-022: Shared copy helper for message content
 *
 * This utility implements the "single source of truth" pattern for copying
 * message content. It handles the fallback chain:
 * 1. If contentRaw present in metadata (persisted), use it
 * 2. Else if artifactRef present in metadata, fetch artifact by ID and use its content
 * 3. Else use message.content (may be truncated)
 *
 * This ensures copy operations return full content even after page reload,
 * using durable pointers (artifactRef) instead of brittle adjacency assumptions.
 */

import { ChatMessage, useChatStore } from '../stores/useChatStore.js';

/**
 * Get the full content for a message, falling back through the artifact chain.
 * Automatically looks up messages from the chat store if not provided.
 *
 * @param message - The message to get content for
 * @param allMessages - Optional: All messages in the session (for artifact lookup).
 *                      If not provided, will be fetched from the chat store.
 * @returns The full content to copy, or truncated content if no artifact available
 */
export function getMessageCopyContent(message: ChatMessage, allMessages?: ChatMessage[]): string {
    // Priority 1: Use contentRaw if available (persisted in metadata)
    if (message.metadata?.contentRaw) {
        return message.metadata.contentRaw;
    }

    // Priority 2: Use artifactRef to look up artifact by ID (durable pointer)
    // This is more reliable than adjacency-based lookup as it survives:
    // - pagination
    // - reload + history hydration
    // - reordering
    // - tool chatter inserted between cards
    const messages = allMessages ?? useChatStore.getState().messages;
    if (message.metadata?.artifactRef && messages) {
        const artifactMsg = messages.find(m => m.id === message.metadata?.artifactRef);
        if (artifactMsg?.toolArgs?.content) {
            return String(artifactMsg.toolArgs.content);
        }
    }

    // Priority 3: Fall back to message.content (may be truncated)
    return message.content;
}

/**
 * Copy message content to clipboard using the fallback chain.
 * This is the single entry point for all copy operations in the UI.
 *
 * @param message - The message to copy
 * @param allMessages - Optional: All messages in the session (for artifact lookup)
 */
export async function copyMessageToClipboard(
    message: ChatMessage,
    allMessages?: ChatMessage[]
): Promise<void> {
    const content = getMessageCopyContent(message, allMessages);
    await navigator.clipboard.writeText(content);
}
